import { useEffect, useRef, useState } from "react";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Pencil,
  MessageCircle,
  Video,
  Heart,
} from "lucide-react";
import { Dialog, DialogContent, DialogTrigger } from "~/components/ui/dialog";
import { Button } from "~/components/ui/button";
import GalleryLayout from "./GridLayout";
import { useFavorite } from "~/lib/hooks/useFavourite";
import VirtualTour from "./VirtualTour";

interface GalleryImage {
  image: string;
  isBuilding: boolean;
  order: number;
  url: string;
}

interface ImageGalleryDialogProps {
  images: GalleryImage[];
  trigger: React.ReactNode;
  liked?: boolean;
  data?: any;
  videoUrl?: string;
  toggleFavorite?: (event?: any) => void;
  listingId?: string | number;
  isBuildingPage?: boolean;
  isPropertyPage?: boolean;
  isBlogPage?: boolean;
  altText?: string;
}

export function ImageGalleryDialog({
  images,
  trigger,
  propertyId,
  data,
  videoUrl,
  listingId,
  isBuildingPage,
  isPropertyPage,
  isBlogPage,
  altText
}: ImageGalleryDialogProps) {
  const id = listingId
  const { liked, toggleFavorite } = useFavorite(id);
  const [open, setOpen] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number | null>(
    null,
  );

  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  // Defer full-size preloads until the gallery opens to avoid blocking the main thread on page load.
  useEffect(() => {
    if (!open) return;
    images.forEach((img) => {
      const preloadImg = new Image();
      preloadImg.src = `https://ik.imagekit.io/officehub/tr:w-2400,f-avif/${img.url}`;
    });
  }, [images, open]);
  const handleImageClick = (index: number) => {
    setCurrentSlideIndex(index);
  };

  const handlePrevSlide = () => {
    if (currentSlideIndex === null) return;
    setCurrentSlideIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextSlide = () => {
    if (currentSlideIndex === null) return;
    setCurrentSlideIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const closeSlider = () => {
    setCurrentSlideIndex(null);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
    touchEndX.current = null;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;
    const distance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;
    if (Math.abs(distance) >= minSwipeDistance) {
      if (distance > 0) {
        handleNextSlide();
      } else {
        handlePrevSlide();
      }
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };



  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className='max-w-full max-h-full bg-black m-auto border-none border-r-0 items-center justify-center'>
        <div
          className="h-screen w-screen bg-black overflow-hidden md:p-4"
          onClick={() => {
            if (currentSlideIndex === null) {
              setOpen(false);
            } else {
              closeSlider();
            }
          }}
        >
          <div className="fixed top-0 left-0 right-0 z-10 flex justify-between p-1 bg-black">
            {/* Left */}
            <div
              className="flex gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              {videoUrl && (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="text-white">
                      <Video className="h-5 w-5" />
                      <span className="sr-only">Video</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-3xl">
                    <VirtualTour url={videoUrl} />
                  </DialogContent>
                </Dialog>
              )}

              {!isBuildingPage && !isPropertyPage && !isBlogPage && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavorite(e);
                  }}
                  aria-label={liked ? "Remove from favorites" : "Add to favorites"}
                >
                  <Heart
                    color="white"
                    className={`h-5 w-5 ${liked ? "fill-oh-secondary" : "fill-oh-black"
                      } duration-1000`}
                  />
                  <span className="sr-only">Like</span>
                </Button>
              )}
            </div>

            {/* Right */}
            <div onClick={(e) => e.stopPropagation()}>
              {currentSlideIndex === null ? (
                <Button
                  variant="ghost"
                  className="text-white hover:bg-gray-black hover:text-white"
                  onClick={() => setOpen(false)}
                >
                  <X className="h-6 w-6" />
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  className="text-white hover:bg-gray-black hover:text-white"
                  onClick={closeSlider}
                >
                  <ChevronLeft className="h-6 w-6" />
                  <span>Back to gallery</span>
                </Button>
              )}
            </div>
          </div>

          {currentSlideIndex === null ? (
            <div className="mt-8 mb-8 flex items-center justify-center overflow-y-auto h-full scrollbar-hide">
              <div
                className="max-w-4xl m-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-4 pb-12">
                  <GalleryLayout
                    images={images}
                    handleImageClick={handleImageClick}
                    altText={altText}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div
              className="md:mt-8 flex items-center justify-center h-full md:h-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className='w-full md:max-w-4xl m-auto'>
                <div className='p-0 md:p-4'>
                  <Button
                    variant='ghost'
                    className='absolute left-4 top-1/2 -translate-y-1/2 text-white bg-black hover:text-white hover:bg-black rounded-full h-12 w-12 hover:border hover:border-white hidden md:flex'
                    onClick={handlePrevSlide}
                  >
                    <ChevronLeft className='h-8 w-8' />
                    <span className='sr-only'>Previous</span>
                  </Button>
                  <div
                    className='flex flex-col items-center'
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  >
                    <div className='w-full md:h-[75vh] md:w-[50vw] relative md:rounded-lg overflow-hidden'>
                      {
                        images.map((img, index) => (
                          <img
                            key={img.url}
                            src={`https://ik.imagekit.io/officehub/tr:w-2400,f-avif/${img.url}`}
                            alt={`${altText} - Image ${img.order}`}
                            className={`md:absolute md:top-0 md:left-0 md:h-full md:w-full md:object-cover w-full h-full object-contain transition-opacity duration-700 ease-in-out ${index === currentSlideIndex ? 'opacity-100 z-10 relative' : 'opacity-0 z-0 absolute top-0 left-0'
                              }`}
                          />
                        ))
                      }
                    </div >
                    <div className='text-white text-sm mt-4'>
                      {currentSlideIndex + 1}/{images.length}
                    </div>
                  </div >
                  <Button
                    variant='ghost'
                    size='icon'
                    className='absolute right-4 top-1/2 -translate-y-1/2 text-white bg-black hover:text-white hover:bg-black rounded-full h-12 w-12 hover:border hover:border-white hidden md:flex'
                    onClick={handleNextSlide}
                  >
                    <ChevronRight className='h-8 w-8' />
                    <span className='sr-only'>Next</span>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div >
      </DialogContent >
    </Dialog >
  );
}
